import { useEffect, useMemo, useState, type ReactNode } from "react";
import {
  Box,
  Check,
  Info,
  Package,
  Pencil,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import ModalBase from "components/ModalBase";
import { Caixa, excluirCaixa, getCaixas } from "services/caixas";
import { formatarMedidas, formatarNumero, pesoCubado } from "utils/caixa";
import FormCaixa from "./Forms/FormCaixa";

type Props = {
  show: boolean;
  title: string;
  setShow?: (value: React.SetStateAction<boolean>) => void;
  setShowNumber?: (value: React.SetStateAction<number>) => void;
  logo?: ReactNode;
};

type Etapa = { tela: "lista" } | { tela: "form"; caixa?: Caixa };

const ModalCaixas = ({ show, title, setShow, setShowNumber, logo }: Props) => {
  const [etapa, setEtapa] = useState<Etapa>({ tela: "lista" });
  const [caixasList, setCaixasList] = useState<Caixa[]>([]);
  const [busca, setBusca] = useState("");
  const [carregando, setCarregando] = useState(false);
  // Exclusao confirma na propria linha: um popover sobre uma tabela de linhas
  // baixas cobre a linha de baixo e esconde justamente o que a pessoa quer
  // conferir antes de apagar.
  const [confirmandoExclusao, setConfirmandoExclusao] = useState<number | null>(
    null,
  );
  const [excluindo, setExcluindo] = useState<number | null>(null);

  const { t, i18n } = useTranslation("dashboard");
  const locale = i18n.language || "pt-BR";

  const fechar = () => {
    if (setShow) setShow(false);
    if (setShowNumber) setShowNumber(-1);
  };

  // Recarrega a cada abertura: a lista muda por fora do modal (cadastro em
  // outra aba, exclusao por outro usuario da conta) e carregar so na montagem
  // deixava a tela desatualizada ate dar F5.
  useEffect(() => {
    if (!show) return;
    setCarregando(true);
    getCaixas()
      .then(setCaixasList)
      .finally(() => setCarregando(false));
  }, [show]);

  // Volta para a lista e limpa a confirmacao pendente sempre que o modal fecha.
  useEffect(() => {
    if (show) return;
    setEtapa({ tela: "lista" });
    setBusca("");
    setConfirmandoExclusao(null);
  }, [show]);

  const addCaixa = (nova: Caixa) => setCaixasList((prev) => [nova, ...prev]);
  const updateCaixa = (nova: Caixa) =>
    setCaixasList((prev) => prev.map((c) => (c.id === nova.id ? nova : c)));

  const caixasFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    if (!termo) return caixasList;
    return caixasList.filter(
      (c) =>
        c.cod_identificacao.toLowerCase().includes(termo) ||
        c.descricao.toLowerCase().includes(termo),
    );
  }, [caixasList, busca]);

  // So mostra a coluna quando a API devolve a contagem (ver Caixa.usos).
  const mostrarUso = caixasList.some((c) => typeof c.usos === "number");

  const remover = (id: number) => {
    setExcluindo(id);
    excluirCaixa(id)
      .then(() => setCaixasList((prev) => prev.filter((c) => c.id !== id)))
      .finally(() => {
        setExcluindo(null);
        setConfirmandoExclusao(null);
      });
  };

  if (etapa.tela === "form") {
    return (
      <ModalBase
        show={show}
        onClose={fechar}
        onVoltar={() => setEtapa({ tela: "lista" })}
        titulo={
          etapa.caixa ? t("modal_boxes.edit_form") : t("modal_boxes.form_title")
        }
        subtitulo={t("modal_boxes.form_subtitle")}
        largura="1000px"
        corpoSemPadding
      >
        <FormCaixa
          key={etapa.caixa?.id ?? "nova"}
          caixa={etapa.caixa}
          action={etapa.caixa ? updateCaixa : addCaixa}
          returnPage={() => setEtapa({ tela: "lista" })}
        />
      </ModalBase>
    );
  }

  return (
    <ModalBase
      show={show}
      onClose={fechar}
      titulo={title}
      icone={logo ?? <Package />}
      subtitulo={
        <>
          {t("modal_boxes.counter", { count: caixasList.length })}
          <span className="mx-1.5 text-slate-300">·</span>
          {t("modal_boxes.counter_hint")}
        </>
      }
      largura="900px"
      corpoSemPadding
      rodape={
        <div className="flex items-start gap-2.5 border-t border-blue-100 bg-blue-50/70 px-6 py-3">
          <Info className="mt-0.5 h-4 w-4 shrink-0 text-brand-primary" />
          <p className="text-[12px] leading-relaxed text-slate-600">
            {t("modal_boxes.cubage_hint_before")}
            <strong className="font-semibold text-slate-700">
              {t("modal_boxes.cubage_hint_term")}
            </strong>
            {t("modal_boxes.cubage_hint_after")}
          </p>
        </div>
      }
    >
      <div className="px-6 py-5">
        {/* BUSCA + CADASTRAR */}
        <div className="mb-4 flex items-center gap-3">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder={t("modal_boxes.search_placeholder")}
              className="h-10 w-full rounded-lg border border-slate-200 bg-white pl-9 pr-3 text-sm text-slate-700 outline-none transition-colors placeholder:text-slate-400 focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
            />
          </div>
          <button
            type="button"
            onClick={() => setEtapa({ tela: "form" })}
            className="flex h-10 shrink-0 items-center gap-2 rounded-lg bg-brand-sidebar px-4 text-sm font-semibold text-white transition-opacity hover:opacity-90"
          >
            <Plus className="h-4 w-4" />
            {t("buttons.register_box")}
          </button>
        </div>

        {/* TABELA */}
        {carregando && caixasList.length === 0 ? (
          <EstadoVazio texto={t("modal_boxes.loading")} />
        ) : caixasFiltradas.length === 0 ? (
          <EstadoVazio
            texto={
              busca
                ? t("modal_boxes.empty_search", { termo: busca })
                : t("modal_boxes.empty")
            }
            acao={
              busca ? undefined : (
                <button
                  type="button"
                  onClick={() => setEtapa({ tela: "form" })}
                  className="mt-3 text-sm font-semibold text-brand-primary hover:underline"
                >
                  {t("buttons.register_box")}
                </button>
              )
            }
          />
        ) : (
          <div className="overflow-hidden rounded-xl border border-slate-200">
            <table className="w-full border-collapse text-left">
              <thead>
                <tr className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500">
                  <th className="px-4 py-2.5 font-semibold">
                    {t("modal_boxes.col_box")}
                  </th>
                  <th className="px-4 py-2.5 font-semibold">
                    {t("modal_boxes.col_size")}
                  </th>
                  <th className="px-4 py-2.5 font-semibold">
                    {t("modal_boxes.col_empty_weight")}
                  </th>
                  <th className="px-4 py-2.5 font-semibold">
                    {t("modal_boxes.col_cubed_weight")}
                  </th>
                  {mostrarUso && (
                    <th className="px-4 py-2.5 font-semibold">
                      {t("modal_boxes.col_usage")}
                    </th>
                  )}
                  <th className="w-28 px-4 py-2.5" />
                </tr>
              </thead>
              <tbody>
                {caixasFiltradas.map((caixa) => {
                  const comprimento = Number(caixa.comprimento);
                  const largura = Number(caixa.largura);
                  const altura = Number(caixa.altura);
                  const confirmando = confirmandoExclusao === caixa.id;

                  return (
                    <tr
                      key={caixa.id}
                      className="border-t border-slate-100 transition-colors hover:bg-slate-50/60"
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <div className="grid h-8 w-8 shrink-0 place-items-center rounded-lg bg-blue-50 text-brand-primary">
                            <Box className="h-4 w-4" />
                          </div>
                          <div className="min-w-0">
                            <p className="truncate text-sm font-semibold text-slate-900">
                              {caixa.descricao}
                            </p>
                            <p className="truncate text-[11px] text-slate-400">
                              {t("modal_boxes.code_label")}{" "}
                              {caixa.cod_identificacao}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-slate-900">
                        {formatarMedidas(comprimento, largura, altura, locale)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">
                        {formatarNumero(Number(caixa.peso), 2, locale)} kg
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-600">
                        {formatarNumero(
                          pesoCubado(comprimento, largura, altura),
                          1,
                          locale,
                        )}{" "}
                        kg
                      </td>
                      {mostrarUso && (
                        <td className="whitespace-nowrap px-4 py-3">
                          {caixa.usos ? (
                            <span className="rounded-full bg-emerald-50 px-2 py-1 text-[11px] font-medium text-emerald-700">
                              {t("modal_boxes.usage_count", {
                                count: caixa.usos,
                              })}
                            </span>
                          ) : (
                            <span className="rounded-full bg-slate-100 px-2 py-1 text-[11px] font-medium text-slate-500">
                              {t("modal_boxes.usage_none")}
                            </span>
                          )}
                        </td>
                      )}
                      <td className="px-4 py-3">
                        {confirmando ? (
                          <div className="flex items-center justify-end gap-1.5">
                            <span className="mr-1 text-[11px] font-medium text-slate-500">
                              {t("modal_boxes.confirm_delete")}
                            </span>
                            <button
                              type="button"
                              aria-label={t("buttons.exclude")}
                              disabled={excluindo === caixa.id}
                              onClick={() => remover(caixa.id)}
                              className="grid h-7 w-7 place-items-center rounded-md border border-red-200 bg-red-50 text-red-600 transition-colors hover:bg-red-100 disabled:opacity-40"
                            >
                              <Check className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              aria-label={t("buttons.cancel")}
                              onClick={() => setConfirmandoExclusao(null)}
                              className="grid h-7 w-7 place-items-center rounded-md border border-slate-200 text-slate-500 transition-colors hover:bg-slate-100"
                            >
                              <X className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end gap-1.5">
                            <button
                              type="button"
                              aria-label={t("buttons.edit")}
                              onClick={() => setEtapa({ tela: "form", caixa })}
                              className="grid h-7 w-7 place-items-center rounded-md border border-slate-200 text-brand-primary transition-colors hover:border-blue-200 hover:bg-blue-50"
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </button>
                            <button
                              type="button"
                              aria-label={t("buttons.exclude")}
                              onClick={() => setConfirmandoExclusao(caixa.id)}
                              className="grid h-7 w-7 place-items-center rounded-md border border-slate-200 text-red-500 transition-colors hover:border-red-200 hover:bg-red-50"
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </ModalBase>
  );
};

export default ModalCaixas;

const EstadoVazio = ({ texto, acao }: { texto: string; acao?: ReactNode }) => (
  <div className="flex flex-col items-center rounded-xl border border-dashed border-slate-200 px-6 py-12 text-center">
    <div className="mb-3 grid h-10 w-10 place-items-center rounded-xl bg-slate-100 text-slate-400">
      <Box className="h-5 w-5" />
    </div>
    <p className="text-sm text-slate-500">{texto}</p>
    {acao}
  </div>
);
